import { useEffect } from 'react';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import { FLOATING_DOCK_HEIGHT_VAR } from '~/modules/root';
import { useNotifications } from '~/modules/notifications';
import { useChatBoxes } from '../hooks/use-chat-boxes';
import { useConversations } from '../hooks/use-conversations';
import { useRealTimeChat } from '../hooks/use-real-time-chat';
import {
    DRAWER_HEADER_HEIGHT_REM,
    DRAWER_MARGIN_REM,
    playNotificationSound,
    SPACING_PX,
} from '../utils';
import { ChatInboxDrawer } from './chat-inbox/ChatInboxDrawer';
import { ChatBox } from './chat-box/ChatBox';

/**
 * The chat inbox drawer and chat boxes for the current user. This component
 * should only be rendered once in the root component. The chat inbox drawer and
 * the chat box states are managed by the `useChatBoxes` hook.
 * @see {@link useChatBoxes}
 */
export function ChatBoxes({ user }: { user: MeUser }) {
    const {
        chatInboxState,
        chatBoxStates,
        isChatBoxesDisabled,
        isChatBoxesHidden,
        isInboxOrChatBoxOpen,
        setChatInboxState,
        openMinimisedChatBox,
        hideInboxAndChatBoxes,
    } = useChatBoxes();
    const {
        conversations,
        isLoading,
        hasMore,
        professionalID,
        branchProfessionalID,
        setInbox,
        setBranch,
        loadMore,
    } = useConversations();
    const { notifications } = useNotifications();

    useRealTimeChat({
        onMessage: (message) => {
            // Play notification sound and open a minimised chat box if a
            // new message is received that wasn't sent by the current user.
            if (
                message.type === 'chat/message' ||
                message.type === 'chat/migrate'
            ) {
                const companyInboxes = user.Professionals.filter(
                    (p) => p.HasInboxAccess,
                ).map((p) => p.ID);
                const isSelfMemberSender =
                    message.sender_type === 'Member' &&
                    message.sender_id === user.ID;
                const isSelfProfessionalSender =
                    message.sender_type === 'Professional' &&
                    companyInboxes.includes(message.sender_id);
                if (
                    !isSelfMemberSender &&
                    !isSelfProfessionalSender &&
                    message.sender_user_id !== user.ID &&
                    !message.is_muted
                ) {
                    const openContext = message.is_member_participant
                        ? null
                        : (message.professional_ids[0] ?? null);
                    openMinimisedChatBox(message.conversation_id, openContext);
                    if (message.is_sound_enabled) {
                        void playNotificationSound();
                    }
                }
            }
        },
    });

    // Publish the collapsed drawer's height while the dock is on screen, so
    // other bottom-right chrome can stack above it instead of duplicating the
    // conditions under which a dock exists at all.
    const isDockVisible =
        !isChatBoxesDisabled &&
        chatInboxState !== 'hidden' &&
        !isChatBoxesHidden;
    useEffect(() => {
        const { style } = document.documentElement;
        if (!isDockVisible) {
            style.removeProperty(FLOATING_DOCK_HEIGHT_VAR);
            return;
        }
        style.setProperty(
            FLOATING_DOCK_HEIGHT_VAR,
            `${DRAWER_HEADER_HEIGHT_REM}rem`,
        );
        // Braces, not a concise body: removeProperty returns the old value, so
        // an expression-bodied arrow types the cleanup as `() => string`.
        return () => {
            style.removeProperty(FLOATING_DOCK_HEIGHT_VAR);
        };
    }, [isDockVisible]);

    // Show the chat messages button when there are new unread messages.
    useEffect(() => {
        if (chatInboxState === 'hidden' && notifications.totalUnreadCount) {
            setChatInboxState('closed');
        }
        // Do not include chatOpenState in the dependencies to prevent
        // re-opening when the chat open state is changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications.totalUnreadCount]);

    if (isChatBoxesDisabled || chatInboxState === 'hidden') {
        return null;
    }

    return (
        <>
            {isInboxOrChatBoxOpen && !isChatBoxesHidden && (
                <div
                    className="fixed inset-0 z-fixed animate-in bg-black/30 duration-300 fade-in"
                    onClick={() => hideInboxAndChatBoxes()}
                />
            )}
            <div
                className={cn(
                    'pointer-events-none fixed inset-x-0 bottom-0 z-fixed flex flex-row-reverse items-end justify-start',
                    // Prevent the floating button from shifting when the scrollbar is locked.
                    '[body[data-scroll-locked]_&]:translate-x-[calc(0%-var(--removed-body-scroll-bar-size,15px))]',
                    isChatBoxesHidden && 'hidden',
                )}
                style={{
                    right: `${DRAWER_MARGIN_REM}rem`,
                    gap: `${SPACING_PX}px`,
                }}
            >
                <ChatInboxDrawer
                    className="pointer-events-auto h-[calc(100dvh-3rem)] min-h-80 shrink-0 transition-transform duration-300"
                    style={{
                        transform:
                            chatInboxState === 'closed'
                                ? `translateY(calc(100% - ${DRAWER_HEADER_HEIGHT_REM}rem))`
                                : undefined,
                    }}
                    conversations={conversations}
                    user={user}
                    professionalID={professionalID}
                    branchProfessionalID={branchProfessionalID}
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onInboxChange={setInbox}
                    onBranchChange={setBranch}
                    onLoadMore={loadMore}
                />
                {chatBoxStates.map((chatBoxState) => (
                    <ChatBox
                        key={`${chatBoxState.id}:${chatBoxState.professionalID}`}
                        className="pointer-events-auto shrink-0"
                        user={user}
                        conversationID={chatBoxState.id}
                        professionalID={chatBoxState.professionalID}
                        conversation={
                            chatBoxState.id > 0 &&
                            chatBoxState.professionalID === professionalID
                                ? conversations?.find(
                                      (c) => c.ID === String(chatBoxState.id),
                                  )
                                : undefined
                        }
                    />
                ))}
            </div>
        </>
    );
}
